export interface TheoryQuestion {
  id: string;
  title: string;
  context: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  points: number;
}

export const STVO_THEORY_QUESTIONS: TheoryQuestion[] = [
  {
    id: 'blinker_anfahren_abbiegen',
    title: 'Was muss die Fahrtrichtungsanzeige (Blinker) rechtzeitig anzeigen?',
    context: 'StVO § 9 & § 10 — Abbiegen und Anfahren',
    options: [
      'Nur das Abbiegen an Kreuzungen',
      'Nur das Anfahren vom Fahrbahnrand',
      'Sowohl das Abbiegen als auch das Anfahren und jeder Fahrstreifenwechsel',
    ],
    correctIndex: 2,
    explanation: 'Gemäß StVO § 9 und § 10 muss jede Richtungsänderung, jeder Spurwechsel und das Anfahren vom Fahrbahnrand rechtzeitig und deutlich mit dem Blinker angekündigt werden.',
    points: 50,
  },
  {
    id: 'rechts_vor_links',
    title: 'Wer hat an einer Kreuzung ohne Verkehrszeichen Vorfahrt?',
    context: 'StVO § 8 Abs. 1 — Grundregel Rechts vor Links',
    options: [
      'Fahrzeuge, die von links einbiegen',
      'Der von rechts Kommende (Rechts vor Links)',
      'Wer schneller an der Haltlinie ankommt',
    ],
    correctIndex: 1,
    explanation: 'StVO § 8 Abs. 1: An Kreuzungen und Einmündungen hat die Vorfahrt, wer von rechts kommt, sofern keine Schilder oder Lichtzeichen etwas anderes regeln.',
    points: 50,
  },
  {
    id: 'kreisverkehr_blinken',
    title: 'Wie verhalten Sie sich beim Blinken im Kreisverkehr (Zeichen 215)?',
    context: 'StVO § 8a — Kreisverkehr',
    options: [
      'Bei der Einfahrt in den Kreisverkehr links blinken',
      'Nur rechtzeitig vor dem Ausfahren aus dem Kreisverkehr rechts blinken',
      'Während der gesamten Kreiselfahrt durchgehend blinken',
    ],
    correctIndex: 1,
    explanation: 'StVO § 8a Abs. 1: Die Einfahrt in den Kreisverkehr erfolgt ohne Fahrtrichtungsanzeiger. Erst beim Verlassen ist das Blinken nach rechts Pflicht.',
    points: 50,
  },
  {
    id: 'gruenpfeil_stop',
    title: 'Wie verhalten Sie sich bei roter Ampel mit Grünpfeilschild (Zeichen 720)?',
    context: 'StVO § 37 Abs. 3 — Grünpfeil',
    options: [
      'Ohne Anhalten vorsichtig mit Schrittgeschwindigkeit rechts abbiegen',
      'Zuerst vollständig an der Haltlinie stoppen, Querverkehr/Fußgänger prüfen, dann abbiegen',
      'Das Zeichen gilt ausschließlich für Einsatzfahrzeuge',
    ],
    correctIndex: 1,
    explanation: 'Vor dem Rechtsabbiegen bei Grünpfeilschild muss stets vollständig an der Haltlinie gestoppt werden (Stopp-Pflicht wie beim Stoppschild).',
    points: 50,
  },
  {
    id: 'durchgezogene_linie',
    title: 'Darf eine durchgehende weiße Linie (Zeichen 295) überfahren werden?',
    context: 'StVO Zeichen 295 — Fahrstreifenbegrenzung',
    options: [
      'Ja, wenn kein Gegenverkehr in Sicht ist',
      'Grundsätzlich nein, sie darf weder überfahren noch überragt werden',
      'Ja, innerorts ist das Überfahren zum Wenden gestattet',
    ],
    correctIndex: 1,
    explanation: 'Zeichen 295: Eine durchgehende Fahrstreifenbegrenzung darf grundsätzlich von keinem Fahrzeug überfahren oder überragt werden.',
    points: 50,
  },
  {
    id: 'schulterblick_toter_winkel',
    title: 'Was gehört zur lückenlosen Verkehrsbeobachtung vor einem Spurwechsel?',
    context: 'StVO § 7 — Fahrstreifenwechsel',
    options: [
      'Ausschließlich ein kurzer Blick in den Rückspiegel',
      'Innenspiegel, Außenspiegel, Blinker setzen und Schulterblick in den toten Winkel',
      'Nur rechtzeitiges Betätigen der Hupe',
    ],
    correctIndex: 1,
    explanation: 'Vor jedem Fahrstreifenwechsel sichert die 3-Spiegel-Kontrolle mit obligatorischem Schulterblick den toten Winkel zuverlässig ab.',
    points: 50,
  },
];

export class TheoryQuizManager {
  private questions: TheoryQuestion[] = [...STVO_THEORY_QUESTIONS];
  private currentQuestionIndex: number = 0;
  private answeredQuestions: Set<string> = new Set();
  public isEnabled: boolean = true;
  public totalScoreBonus: number = 0;

  public getNextQuestion(): TheoryQuestion | null {
    if (!this.isEnabled) return null;
    for (let i = 0; i < this.questions.length; i++) {
      const idx = (this.currentQuestionIndex + i) % this.questions.length;
      const q = this.questions[idx];
      if (!this.answeredQuestions.has(q.id)) {
        this.currentQuestionIndex = (idx + 1) % this.questions.length;
        return q;
      }
    }
    // If all answered, reset answered set to allow continuous practice
    this.answeredQuestions.clear();
    return this.questions[0];
  }

  public answerQuestion(questionId: string, selectedIndex: number): { isCorrect: boolean; explanation: string; points: number } {
    const q = this.questions.find((item) => item.id === questionId);
    if (!q) {
      return { isCorrect: false, explanation: '', points: 0 };
    }
    this.answeredQuestions.add(questionId);
    const isCorrect = selectedIndex === q.correctIndex;
    const pts = isCorrect ? q.points : 0;
    if (isCorrect) {
      this.totalScoreBonus += pts;
    }
    return { isCorrect, explanation: q.explanation, points: pts };
  }

  public reset() {
    this.answeredQuestions.clear();
    this.currentQuestionIndex = 0;
    this.totalScoreBonus = 0;
  }
}
